import {FC, useEffect, useRef, useState, useCallback} from 'react';
import AurDataGrid from '../../common/aur-data-grid/AurDataGrid';
import {GridSortModel} from '@mui/x-data-grid';
import {Grid} from '@mui/material';
import {getColumns, HostingUserSummaryData} from './model';
import AurSelectDropdown from '../../common/aur-select-dropdown/AurSelectDropdown';
import {t} from 'i18next';
import AurChipList, {AurChipConfig} from '../../common/aur-chip/AurChipList';
import {useSelector} from 'react-redux';
import {
  clearFilters,
  getHostingUserSummary,
  getLastAccessedDateList,
  getSavedFilters,
  getUsernamesList,
  saveFilters,
} from '../../../api/ReportingManagement';
import {RootState} from '../../../redux/store';
import {getLocalFormatedDate} from '../../../utils/utils';
import AurPopup from '../../common/aur-popup/AurPopup';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  CUSTOM_REPORT_COL_HEIGHT,
  failedToLoadStatusCodes,
  INVOICE_REPORT_GRID_ERROR_COMPONENT,
  TABLE_PER_PAGE_DEFAULT_50_ROW,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../constants/constants';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {TABLE_PAGE_SIZE_OPTION} from '../../../constants/constants';
import AurDataGridLoadingOverlay from '../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import {debounce} from 'lodash';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_REPORT_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

interface IFiltersData {
  userNames?: string[];
  lastAccessDates?: string[];
}

const HostingUserSummaryReportTable: FC = () => {
  const {trackGAEvent} = useAnalytics();

  //Handle error when api fails
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(
    (tableContainerRef.current?.offsetHeight ?? 500) +
      INVOICE_REPORT_GRID_ERROR_COMPONENT
  );
  const DEBOUNCE_DELAY = 300;
  const [isFailedToLoadUserSummary, setIsFailedToLoadUserSummary] =
    useState<boolean>(false);
  const [currentPageGrid, setCurrentPageGrid] = useState<number>(1);
  const [itemsPerPageGrid, setItemsPerPageGrid] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [hasFilterValueChanged, setHasFilterValueChanged] = useState(true);

  // Updated initial state with empty values for both filters

  const [totalCount, setTotalCount] = useState<number>(0);
  const [userList, setUserList] = useState([]);
  const [userSummaryList, setUserSummaryList] = useState([]);
  const [lastAccessedDateList, setLastAccessedDateList] = useState<any>([]);
  const [clearAllModal, setClearAllModal] = useState<boolean>(false);
  const [resetToDefaultModal, setResetToDefaultModal] =
    useState<boolean>(false);
  const reportId = 3;
  const {clientId, projectId, workspaceId} = useSelector(
    (state: RootState) => state.reports
  );
  const [isFetchLastAccessedDateLoading, setIsFetchLastAccessedDateLoading] =
    useState(false);
  const [isFetchUsersLoading, setIsFetchUsersLoading] = useState(false);
  const [isFetchSavedFiltersLoading, setIsFetchSavedFiltersLoading] =
    useState(false);
  const [
    isFetchHostingUserSummaryReportLoading,
    setIsFetchHostingUserSummaryReportLoading,
  ] = useState(false);
  const [isOnSaveFilterLoading, setIsOnSaveFilterLoading] = useState(false);
  const [isClearAllFilterLoading, setIsClearAllFilterLoading] = useState(false);
  const [isFetchUserSummaryReportInitial, setIsFetchUserSummaryReportInitial] =
    useState(true);
  const apiResponseTime = useRef('');
  const dataLayerPushed = useRef(false);
  const buildParamString = ({
    clientId,
    projectId,
    workspaceId,
    reportId,
  }: any) => {
    return `?clientId=${clientId}&projectId=${projectId}&workspaceId=${workspaceId}&reportId=${reportId}`;
  };

  const createChipConfigData = (data?: IFiltersData) => {
    const convertNames: string[] =
      data?.userNames?.map((name) => name.split(' ').join(', ')) || [];

    const mapSelections = (items: any, selectedIds: any) =>
      items
        .filter((item: any) => selectedIds?.includes(item.name))
        .map((item: any) => ({id: item.id, value: item.name}));
    return [
      {
        id: 1,
        filterSelection: data?.userNames?.length
          ? mapSelections(userList, convertNames)
          : [],
        filterName: 'User',
        showRemove: true,
        onRemove: () => onRemoveFilter(1),
      },
      {
        id: 2,
        filterSelection: data?.lastAccessDates?.length
          ? mapSelections(lastAccessedDateList, data?.lastAccessDates)
          : [],
        showRemove: true,
        filterName: 'Last Accessed Date',
        onRemove: () => onRemoveFilter(2),
      },
    ];
  };

  const [chipConfigs, setChipsConfig] = useState<AurChipConfig[]>(
    createChipConfigData()
  );

  const setFiltersAndChipData = (data: IFiltersData) => {
    setChipsConfig(createChipConfigData(data));
  };

  const fetchSavedFilters = async () => {
    setIsFetchSavedFiltersLoading(true);
    try {
      if (projectId && workspaceId && clientId && reportId) {
        const paramString = buildParamString({
          clientId,
          projectId,
          workspaceId,
          reportId,
        });

        const result = await getSavedFilters(paramString);
        if (result?.data?.data) {
          setFiltersAndChipData(result.data.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch saved filters:', error);
    } finally {
      setIsFetchSavedFiltersLoading(false);
    }
    setHasFilterValueChanged(false);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setIsFetchUsersLoading(true);
      try {
        const userResponse = await getUsernamesList(
          clientId!,
          projectId!,
          workspaceId!
        );

        const userData = await userResponse?.data?.data?.map(
          (data: string, index: number) => {
            return {
              id: index,
              name: data,
            };
          }
        );
        setUserList(userData);
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetchUsersLoading(false);
      }
    };
    const fetchlastAccessedDate = async () => {
      setIsFetchLastAccessedDateLoading(true);
      try {
        const dateResponse = await getLastAccessedDateList(
          clientId!,
          projectId!,
          workspaceId!
        );
        dateResponse?.data?.data?.push(HostingUserSummaryData.none);
        const dateData = await dateResponse?.data?.data?.map(
          (data: string, index: number) => {
            return {
              id: index,
              name:
                data === HostingUserSummaryData.none
                  ? data
                  : getLocalFormatedDate(data),
            };
          }
        );
        setLastAccessedDateList(dateData);
      } catch (error: any) {
        console.error(error);
        if (error.status === 404) {
          const additionalDate = {
            id: 0,
            name: HostingUserSummaryData.none,
          };
          setLastAccessedDateList([additionalDate]);
        }
      } finally {
        setIsFetchLastAccessedDateLoading(false);
      }
    };
    fetchUsers();
    fetchlastAccessedDate();
  }, [clientId, projectId, workspaceId]);

  useEffect(() => {
    if (lastAccessedDateList.length !== 0 || userList.length !== 0) {
      fetchSavedFilters();
    }
  }, [lastAccessedDateList, userList]);

  const requestTimestampRef = useRef(0);

  const fetchUserSummaryReport = async () => {
    const currentRequestTimestamp = Date.now();
    requestTimestampRef.current = currentRequestTimestamp;
    setIsFetchHostingUserSummaryReportLoading(true);
    setUserSummaryList([]);
    setIsFailedToLoadUserSummary(false);
    try {
      if (workspaceId && projectId && clientId) {
        const userData = chipConfigs?.[0]?.filterSelection.length
          ? chipConfigs?.[0]?.filterSelection
              .map((item) => item.value)
              .join('|')
          : '';

        const lastAccessedDate = chipConfigs?.[1]?.filterSelection.length
          ? chipConfigs?.[1]?.filterSelection
              .map((item) => item.value)
              .join('|')
          : '';
        const userResponse = await getHostingUserSummary(
          clientId,
          projectId,
          workspaceId,
          userData,
          lastAccessedDate,
          currentPageGrid,
          itemsPerPageGrid
        );
        const resulData = userResponse?.data?.data?.map(
          (data: any, index: number) => {
            return {...data, id: index};
          }
        );

        apiResponseTime.current = userResponse.data.responseTime;

        if (currentRequestTimestamp === requestTimestampRef.current) {
          const totalRowsCount =
            userResponse?.data?.meta?.pagination?.totalItems;
          setTotalCount(totalRowsCount);
          setUserSummaryList(resulData);
          if (
            isFetchUserSummaryReportInitial &&
            totalRowsCount > TABLE_PER_PAGE_DEFAULT_50_ROW
          ) {
            setItemsPerPageGrid(TABLE_PER_PAGE_DEFAULT_50_ROW);
            setIsFetchUserSummaryReportInitial(false);
          }
        }
      }
    } catch (error: any) {
      if (failedToLoadStatusCodes.includes(error.status))
        setIsFailedToLoadUserSummary(true);
      setUserSummaryList([]);
      apiResponseTime.current = error.responseTime;
    } finally {
      if (currentRequestTimestamp === requestTimestampRef.current) {
        setTimeout(() => {
          setIsFetchHostingUserSummaryReportLoading(false);
        }, 1000);
      }
      if (dataLayerPushed.current === false) {
        dataLayerPushed.current = true;
        trackGAEvent({
          eventName: GA_EVENT_NAMES.PAGE_VIEW,
          page: GA_DATA_MODE_AUTO,
          user: GA_DATA_MODE_AUTO,
          report: {
            name: t('gaTracking.reports.tabHostingUserSummary'),
            type: GA_REPORT_TYPE.STANDARD,
            load_time: apiResponseTime.current,
          },
        });
      }
    }
  };

  useEffect(() => {
    fetchUserSummaryReport();
  }, [clientId, projectId, workspaceId]);

  const debouncedFetchUserSummaryReport = useCallback(
    debounce(() => {
      fetchUserSummaryReport();
    }, DEBOUNCE_DELAY),
    [
      currentPageGrid,
      itemsPerPageGrid,
      clientId,
      projectId,
      workspaceId,
      chipConfigs,
    ]
  );

  useEffect(() => {
    debouncedFetchUserSummaryReport();
    return () => {
      debouncedFetchUserSummaryReport.cancel();
    };
  }, [
    debouncedFetchUserSummaryReport,
    currentPageGrid,
    itemsPerPageGrid,
    clientId,
    projectId,
    workspaceId,
    chipConfigs,
  ]);

  const onSetSortModel = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  const handleOnSave = () => {
    onSaveFilters();
  };

  const onSaveFilters = async () => {
    setIsOnSaveFilterLoading(true);
    try {
      const paramString = `?clientId=${clientId}&projectId=${projectId}&workspaceId=${workspaceId}`;
      const formatDates = chipConfigs?.[1]?.filterSelection.map((data) => {
        if (data.value === HostingUserSummaryData.none) {
          return data.value;
        }
        return getLocalFormatedDate(data.value);
      });
      const formatuserNames = chipConfigs?.[0]?.filterSelection?.map((data) =>
        data.value
          .split(', ')
          .map((part) => part)
          .join(' ')
      );
      const requestBody = {
        ...(formatuserNames.length > 0 && {userNames: formatuserNames}),
        ...(formatDates.length > 0 && {lastAccessDates: formatDates}),
      };
      await saveFilters(reportId, paramString, requestBody);
      const savedFilters = requestBody as any;
      setFiltersAndChipData(savedFilters);
      setHasFilterValueChanged(false);
    } catch (error) {
      console.error('Failed to save filters:', error);
    } finally {
      setIsOnSaveFilterLoading(false);
    }
  };

  const onClearFilters = async () => {
    setIsClearAllFilterLoading(true);
    try {
      if (projectId && workspaceId && clientId && reportId) {
        const paramString = `?clientId=${clientId}&projectId=${projectId}&workspaceId=${workspaceId}`;
        await clearFilters(reportId, paramString);
        handleClearAllModalOptionSelected();
      }
    } catch (error) {
      console.error('Failed to Clear saved filters:', error);
    } finally {
      setIsClearAllFilterLoading(false);
    }
  };

  const handleOnClearAll = () => {
    setClearAllModal(true);
  };

  const handleResetToDefaultModalOptionSelected = () => {
    closeModalHandler();
    onClearFilters();
    trackGAEvent({
      eventName: GA_EVENT_NAMES.FILTER,
      click: {
        name: t('gaTracking.global.resetToDefault'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.reports.filterReport'),
      },
    });
  };

  const handleResetToDefault = () => {
    setResetToDefaultModal(true);
  };

  const closeModalHandler = () => {
    setClearAllModal(false);
    setResetToDefaultModal(false);
  };

  const onRemoveFilter = (filterId: number) => {
    setHasFilterValueChanged(true);
    setChipsConfig((prevConfig) =>
      prevConfig.map((filter) =>
        filter.id === filterId ? {...filter, filterSelection: []} : filter
      )
    );
  };

  const handleClearAllModalOptionSelected = () => {
    closeModalHandler();
    setChipsConfig((prevConfig) =>
      prevConfig.map((filter) => ({...filter, filterSelection: []}))
    );
    trackGAEvent({
      eventName: GA_EVENT_NAMES.FILTER,
      click: {
        name: t('gaTracking.global.clearAll'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.reports.filterReport'),
      },
    });
  };

  const handleOnDropdownDataChanged = (
    id: number,
    filterData: {name: string; id: number}[]
  ) => {
    const updatedChipConfigs = [...chipConfigs];
    updatedChipConfigs[id].filterSelection = filterData.map((item) => ({
      id: item.id,
      value: item.name,
    }));

    setChipsConfig(updatedChipConfigs);
    setHasFilterValueChanged(true);
  };

  //  To get datagrid height
  useEffect(() => {
    const updateHeight = () => {
      if (tableContainerRef.current) {
        setSize(
          tableContainerRef.current.offsetHeight - CUSTOM_REPORT_COL_HEIGHT
        );
      }
    };
    window.addEventListener('resize', updateHeight);
    updateHeight();

    return () => window.removeEventListener('resize', updateHeight);
  });

  return (
    <div>
      <div className="user-summary-report-table">
        <Grid container gap={1} className="user-summary-dropdown">
          {/* User Dropdown */}
          <Grid item xs={6} md={3}>
            <AurSelectDropdown
              items={userList}
              searchPlaceHolder={t('userHomePage.search')}
              itemsCountwithNoScroll={3}
              itemDisplay="name"
              itemKey="id"
              selectAllLabel={t('common.selectAll')}
              dropdownLabel={t('reports.hostingUserSummary.filterUser')}
              onSelectionChange={(data) => {
                handleOnDropdownDataChanged(0, data as any);
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.FILTER,
                  click: {
                    name: t('reports.hostingUserSummary.filterUser'),
                    type: GA_CLICK_TYPES.DROPDOWN,
                    region: GA_REGIONS.BODY,
                    action: t('gaTracking.reports.filterReport'),
                  },
                });
              }}
              defaultSelection={chipConfigs?.[0]?.filterSelection?.map(
                (item) => {
                  return {
                    id: item.id,
                    name: item.value,
                  };
                }
              )}
              hasValidationError={false}
              validationText=""
              helperText=""
              aria-label={t('reports.hostingUserSummary.filterUser')}
              disabled={userList?.length === 0}
              clearValues={chipConfigs?.[0]?.filterSelection.length === 0}
              onClearClicked={() => onRemoveFilter(1)}
              data-testId="aur-test-user-dropdown"
              search={userList?.length > 11}
              hintText={t('reports.hostingUserSummary.filterUserHint')}
            />
          </Grid>

          {/* Last Accessed Date Dropdown */}
          <Grid item xs={6} md={3}>
            <AurSelectDropdown
              items={lastAccessedDateList}
              searchPlaceHolder={t('userHomePage.search')}
              itemsCountwithNoScroll={3}
              itemDisplay="name"
              itemKey="id"
              selectAllLabel={t('common.selectAll')}
              dropdownLabel={t(
                'reports.hostingUserSummary.filterLastAccesedDate'
              )}
              onSelectionChange={(data) => {
                handleOnDropdownDataChanged(1, data as any);
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.FILTER,
                  click: {
                    name: t('gaTracking.reports.filterLastAccesedDate'),
                    type: GA_CLICK_TYPES.DROPDOWN,
                    region: GA_REGIONS.BODY,
                    action: t('gaTracking.reports.filterReport'),
                  },
                });
              }}
              defaultSelection={chipConfigs?.[1]?.filterSelection?.map(
                (item) => {
                  return {
                    id: item.id,
                    name: item.value,
                  };
                }
              )}
              hasValidationError={false}
              validationText=""
              helperText=""
              aria-label={t('reports.hostingUserSummary.filterLastAccesedDate')}
              disabled={lastAccessedDateList?.length === 0}
              clearValues={chipConfigs?.[1]?.filterSelection.length === 0}
              onClearClicked={() => onRemoveFilter(2)}
              search={lastAccessedDateList?.length > 11}
              hintText={t('reports.hostingUserSummary.filterDateHint')}
            />
          </Grid>
        </Grid>

        {/* Display Chips */}
        {(chipConfigs[0].filterSelection.length > 0 ||
          chipConfigs[1].filterSelection.length > 0) && (
          <div className="report-chip-container">
            <AurChipList
              onResetToDefault={handleResetToDefault}
              onSave={handleOnSave}
              onClearAll={handleOnClearAll}
              hasFilterValueChanged={hasFilterValueChanged}
              chipConfigs={chipConfigs}
              isOnSaveLoading={isOnSaveFilterLoading}
              isClearAllLoading={isClearAllFilterLoading}
              trackFilter={true}
            />
          </div>
        )}

        {/* Data Grid */}
        <div className="report-table-container">
          <AurDataGrid
            className="reports-grid"
            rows={userSummaryList}
            getRowId={(row) => row.id}
            columns={getColumns()}
            pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
            visibleToolTip={true}
            checkBoxVisible={false}
            entityType={''}
            pointer={true}
            currentPage={currentPageGrid}
            setCurrentPage={setCurrentPageGrid}
            itemsPerPage={itemsPerPageGrid}
            setItemsPerPage={setItemsPerPageGrid}
            sortModel={sortModel}
            setSortModel={onSetSortModel}
            totalRowCount={totalCount}
            sortingMode="server"
            paginationMode="server"
            rowSelection={false}
            aria-label={t('accessibility.table.hostingUserSummary')}
            headerSpanTabIndex={-1}
            defaultHeaderCellTabIndex={-1}
            disableHeaderRowTabIndex
            getRowHeight={() => 56}
            columnHeaderHeight={64}
            onRefresh={() => fetchUserSummaryReport()}
            containerHeight={size}
            noRowsOverlay={
              isFetchHostingUserSummaryReportLoading
                ? AurDataGridLoadingOverlay
                : isFailedToLoadUserSummary
                  ? AurDataGridErrorOverlay
                  : undefined
            }
          />
        </div>
      </div>
      {clearAllModal && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={clearAllModal}
          onOk={handleClearAllModalOptionSelected}
          onCancel={closeModalHandler}
          popupHeading={t('common.clearAllPopup.clearHeading')}
          popupContent={t('common.clearAllPopup.popupContent')}
          popupTitle={t('common.clearAllPopup.popupTitle')}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={true}
          labelActionCancel={t('common.clearAllPopup.buttonCancel')}
          labelActionOk={t('common.clearAllPopup.buttonYes')}
          exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
          continueButtonHint={t('common.clearAllPopup.confirmToClearHint')}
          cancelButtonTitle=""
        />
      )}
      {resetToDefaultModal && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={resetToDefaultModal}
          onOk={handleResetToDefaultModalOptionSelected}
          onCancel={closeModalHandler}
          popupHeading={t('common.clearAllPopup.ResetHeading')}
          popupContent={t('common.clearAllPopup.popupContent')}
          popupTitle={t('common.clearAllPopup.popupTitle')}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={true}
          labelActionCancel={t('common.clearAllPopup.buttonCancel')}
          labelActionOk={t('common.clearAllPopup.ResetYes')}
          cancelButtonTitle=""
          exitButtonHint={t('common.clearAllPopup.cancelResetToDefaultHint')}
          continueButtonHint={t(
            'common.clearAllPopup.confirmResetToDefaultHint'
          )}
        />
      )}
      {
        <AurLoadingIndicator
          overlay={true}
          isOpen={
            isFetchSavedFiltersLoading ||
            isFetchLastAccessedDateLoading ||
            isFetchUsersLoading
          }
        />
      }
    </div>
  );
};

export default HostingUserSummaryReportTable;
